import { v4 as uuidv4 } from 'uuid';
import { ValidationError } from '../utils/errors.js';
import { validateColor } from '../utils/validators.js';

/**
 * Represents a type of booking with visual styling and restrictions
 */
export class BookingType {
  /**
   * Create a new BookingType
   * @param {Object} data - BookingType configuration
   * @param {string} [data._id] - Unique identifier (auto-generated if not provided)
   * @param {string} data.name - Display name for the booking type
   * @param {string} data.backgroundColor - Background color (hex color)
   * @param {string} data.foregroundColor - Text/foreground color (hex color)
   * @param {string} [data.description] - Optional description
   * @param {number} [data.priority=1] - Numeric priority for conflicts (higher = more important)
   */
  constructor(data) {
    if (!data || typeof data !== 'object') {
      throw new ValidationError('BookingType data is required');
    }

    if (!data.name || typeof data.name !== 'string') {
      throw new ValidationError('Name is required', 'name', data.name);
    }

    if (!data.backgroundColor) {
      throw new ValidationError('Background color is required', 'backgroundColor', data.backgroundColor);
    }

    if (!data.foregroundColor) {
      throw new ValidationError('Foreground color is required', 'foregroundColor', data.foregroundColor);
    }

    // Validate colors
    validateColor(data.backgroundColor, 'backgroundColor');
    validateColor(data.foregroundColor, 'foregroundColor');

    this._id = data._id || uuidv4();
    this.name = data.name.trim();
    this.backgroundColor = data.backgroundColor;
    this.foregroundColor = data.foregroundColor;
    this.description = data.description ? data.description.trim() : '';
    this.priority = typeof data.priority === 'number' ? data.priority : 1;

    // Validate priority range
    if (this.priority < 1 || this.priority > 100) {
      throw new ValidationError('Priority must be between 1 and 100', 'priority', this.priority);
    }
  }

  /**
   * Update booking type properties
   * @param {Object} updates - Properties to update
   * @returns {BookingType} This instance for chaining
   */
  update(updates) {
    if (!updates || typeof updates !== 'object') {
      throw new ValidationError('Updates object is required');
    }

    // Validate updates without applying them first
    const allowedFields = ['name', 'backgroundColor', 'foregroundColor', 'description', 'priority'];
    const updateKeys = Object.keys(updates);

    for (const key of updateKeys) {
      if (!allowedFields.includes(key)) {
        throw new ValidationError(`Field '${key}' cannot be updated`, key, updates[key]);
      }
    }

    // Validate specific field types
    if (updates.name !== undefined) {
      if (!updates.name || typeof updates.name !== 'string') {
        throw new ValidationError('Name must be a non-empty string', 'name', updates.name);
      }
    }

    if (updates.backgroundColor !== undefined) {
      validateColor(updates.backgroundColor, 'backgroundColor');
    }

    if (updates.foregroundColor !== undefined) {
      validateColor(updates.foregroundColor, 'foregroundColor');
    }

    if (updates.priority !== undefined) {
      if (typeof updates.priority !== 'number' || updates.priority < 1 || updates.priority > 100) {
        throw new ValidationError('Priority must be a number between 1 and 100', 'priority', updates.priority);
      }
    }

    // Apply updates
    if (updates.name !== undefined) {
      this.name = updates.name.trim();
    }
    if (updates.backgroundColor !== undefined) {
      this.backgroundColor = updates.backgroundColor;
    }
    if (updates.foregroundColor !== undefined) {
      this.foregroundColor = updates.foregroundColor;
    }
    if (updates.description !== undefined) {
      this.description = updates.description ? updates.description.trim() : '';
    }
    if (updates.priority !== undefined) {
      this.priority = updates.priority;
    }

    return this;
  }

  /**
   * Convert to JSON string for persistence
   * @returns {string} JSON string representation
   */
  toJSON() {
    const data = {
      _id: this._id,
      name: this.name,
      backgroundColor: this.backgroundColor,
      foregroundColor: this.foregroundColor,
      description: this.description,
      priority: this.priority
    };
    return JSON.stringify(data);
  }

  /**
   * Create BookingType instance from JSON string
   * @param {string} jsonString - JSON string data
   * @returns {BookingType} New BookingType instance
   */
  static fromJSON(jsonString) {
    if (!jsonString || typeof jsonString !== 'string') {
      throw new ValidationError('Valid JSON string is required');
    }

    let jsonData;
    try {
      jsonData = JSON.parse(jsonString);
    } catch (error) {
      throw new ValidationError('Invalid JSON string format');
    }

    return new BookingType(jsonData);
  }

  /**
   * Get default booking types
   * @returns {Array<BookingType>} Array of default booking types
   */
  static getDefaults() {
    return [
      new BookingType({
        _id: 'regular',
        name: 'Regular Booking',
        backgroundColor: '#4CAF50',
        foregroundColor: '#FFFFFF',
        description: 'Standard green booking',
        priority: 1
      }),
      new BookingType({
        _id: 'maintenance',
        name: 'Maintenance',
        backgroundColor: '#FF9800',
        foregroundColor: '#000000',
        description: 'Green maintenance and repairs',
        priority: 10
      }),
      new BookingType({
        _id: 'tournament',
        name: 'Tournament',
        backgroundColor: '#9C27B0',
        foregroundColor: '#FFFFFF',
        description: 'Tournament or competition booking',
        priority: 5
      })
    ];
  }
} 